import React from 'react';
import { Typography, Box, Checkbox } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { useDispatch } from 'react-redux';
import GCSBucketFields from './GCSBucketFields';
import DataRequirementDocumentChip from '../../DataRequirementDocumentChip';

interface GCSBucketDetailsProps {
  loading: boolean;
  formValues: Record<string, string | string[]>;
  handleInputChange: (name: string, value: string | string[]) => void;
  setIsFormValid: (isValid: boolean) => void;
  handleMonthlyRefreshChange: () => void;
  monthlyRefreshStatus: boolean;
}

const GCSBucketDetails: React.FC<GCSBucketDetailsProps> = ({
  loading,
  formValues,
  handleInputChange,
  setIsFormValid,
  handleMonthlyRefreshChange,
  monthlyRefreshStatus,
}) => {
  const dispatch = useDispatch();
  const handleCopyToClipboard = (text: string) => {
    dispatch(
      setToastMessage({
        type: TOAST_TYPES.SUCCESS,
        message: 'Copied',
      }),
    );
    navigator.clipboard.writeText(text);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '4rem',
        width: '100%',
      }}
    >
      <Box
        sx={{
          flex: 1,
          py: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              color: '#162936',
            }}
          >
            Please upload your IAP, IAA, and UA spend data as CSVs to a GCS bucket and share access.
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.375rem',
              color: '#6C6C6C',
            }}
          >
            File format must follow the naming convention:
          </Typography>
          <ul
            style={{
              paddingLeft: '2rem',
              margin: 0,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            <li>{'<Game_Name>_<IAP>_<YYYY_MM_DD_hh_mm>.csv'}</li>
            <li>{'<Game_Name>_<IAA>_<YYYY_MM_DD_hh_mm>.csv'}</li>
            <li>{'<Game_Name>_<UA>.csv'}</li>
          </ul>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
          }}
        >
          <GCSBucketFields
            isEditable={true}
            formValues={formValues}
            handleInputChange={handleInputChange}
            onValidityChange={setIsFormValid}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              color: '#162936',
            }}
          >
            Grant read access to
          </Typography>
          <Box
            sx={{
              background: '#2F736E1F',
              borderRadius: '0.25rem',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: 'fit-content',
              maxWidth: '80%',
            }}
          >
            <Typography
              fontSize={'0.75rem'}
              fontWeight={700}
              lineHeight={'1rem'}
              color={'#162936'}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
              whiteSpace={'nowrap'}
            >
              pvx-data-loader@pvx-dev.iam.gserviceaccount.com
            </Typography>

            <ContentCopyIcon
              onClick={() =>
                handleCopyToClipboard('pvx-data-loader@pvx-dev.iam.gserviceaccount.com')
              }
              sx={{
                ':hover': {
                  cursor: 'pointer',
                },
              }}
            ></ContentCopyIcon>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.25rem',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={monthlyRefreshStatus}
              onChange={handleMonthlyRefreshChange}
              sx={{
                color: '#7FBCAD',
              }}
            />
            <Typography
              fontSize="0.875rem"
              lineHeight="1.25rem"
              fontWeight={700}
              color="#162936"
              letterSpacing="-0.35px"
            >
              I authorise PvX to securely refresh my data each month for accurate reporting.
            </Typography>
          </Box>
          <ul
            style={{
              paddingLeft: '2rem',
              margin: 0,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            <li>
              Refresh the data tables by 5th of every month with the latest data to continue
              accessing Benchmarks and Financial Projections.
            </li>
            <li>Dashboards will be refreshed monthly by the 10th of each month.</li>
            <li>You can turn this off any time from ‘Settings’.</li>
          </ul>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          borderRadius: '0.5rem',
          background: '#F0F0F1',
          padding: '1rem',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.5rem',
            lineHeight: '1.875rem',
            color: '#6C6C6C',
          }}
        >
          Note the following for hassle free integration
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#6C6C6C',
            }}
          >
            Download the Data Requirement document.
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#6C6C6C',
            }}
          >
            This document details the data requirement, format and sharing mechanism.
          </Typography>
          <DataRequirementDocumentChip
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #2F736E1F',
            }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#162936',
              marginBottom: '0.75rem',
            }}
          >
            Ensure:
          </Typography>
          <ul
            style={{
              paddingLeft: '2rem',
              margin: 0,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            <li>Tables must follow the schema defined in Data Requirement Document.</li>
            <li>Columns like user_id, transaction_date, and gross/net revenue are present.</li>
            <li>Table values reconcile with your MIS.</li>
            <li>No personal identifiable data is included.</li>
          </ul>
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#162936',
              marginBottom: '0.75rem',
            }}
          >
            Security & Privacy Notice:
          </Typography>
          <ul
            style={{
              paddingLeft: '2rem',
              margin: 0,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            <li>Access is read-only and restricted to the provided tables.</li>
            <li>Your data is encrypted during transfer and only used for underwriting.</li>
            <li>You can revoke access at any time after the evaluation.</li>
          </ul>
        </Box>
      </Box>
    </Box>
  );
};

export default GCSBucketDetails;
